import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "../../services/api";
import { AUTOPILOT_COLORS } from "./styles";
import { countMatches, withSearch } from "./searchHighlight";

function highlightJson(raw, query) {
  const result = [];
  let key = 0;
  const lines = raw.split("\n");
  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const tokens = [];
    let remaining = line;
    while (remaining.length > 0) {
      // chave: "foo":
      const keyMatch = /^(\s*)("(?:\\.|[^"\\])*")(\s*:)/.exec(remaining);
      if (keyMatch) {
        if (keyMatch[1]) tokens.push({ type: "text", text: keyMatch[1] });
        tokens.push({ type: "attr", text: keyMatch[2] });
        tokens.push({ type: "text-dim", text: keyMatch[3] });
        remaining = remaining.slice(keyMatch[0].length);
        continue;
      }
      // string
      const strMatch = /^"(?:\\.|[^"\\])*"/.exec(remaining);
      if (strMatch) {
        tokens.push({ type: "attrval", text: strMatch[0] });
        remaining = remaining.slice(strMatch[0].length);
        continue;
      }
      // número
      const numMatch = /^-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/.exec(remaining);
      if (numMatch) {
        tokens.push({ type: "tag", text: numMatch[0] });
        remaining = remaining.slice(numMatch[0].length);
        continue;
      }
      // boolean/null
      const litMatch = /^(true|false|null)\b/.exec(remaining);
      if (litMatch) {
        tokens.push({ type: "prolog", text: litMatch[0] });
        remaining = remaining.slice(litMatch[0].length);
        continue;
      }
      tokens.push({ type: "text", text: remaining[0] });
      remaining = remaining.slice(1);
    }
    for (const tok of tokens) {
      let color;
      if (tok.type === "attr") color = "var(--everlog-code-attr)";
      else if (tok.type === "attrval") color = "var(--everlog-code-attrval)";
      else if (tok.type === "tag") color = "var(--everlog-code-tag)";
      else if (tok.type === "prolog") color = "var(--everlog-code-prolog)";
      else if (tok.type === "text-dim") color = "var(--everlog-code-text-dim)";
      else color = "var(--everlog-code-text)";
      const kb = key++;
      result.push(<span key={kb} style={{ color }}>{query ? withSearch(tok.text, query, kb) : tok.text}</span>);
    }
    if (li < lines.length - 1) result.push(<br key={key++} />);
  }
  return result;
}

function PayloadViewerModal({ isOpen, onClose, chaveacesso, numero, embarcadorId }) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(function() {
    if (!isOpen || !chaveacesso) return;
    setLoading(true);
    setPayload(null);
    setError(null);
    setSearch("");
    const params = { chaveacesso };
    if (embarcadorId) params.embarcadorId = embarcadorId;
    api.get("/autopilot/insights/nf/payload", { params })
      .then(function(res) {
        setPayload(res.data.payload);
        setLoading(false);
      })
      .catch(function(err) {
        setError((err.response && err.response.data && err.response.data.error) || "Erro ao carregar payload");
        setLoading(false);
      });
  }, [isOpen, chaveacesso, embarcadorId]);

  const formatted = payload ? JSON.stringify(payload, null, 2) : "";
  const matchCount = countMatches(formatted, search);

  function handleCopy() {
    if (!formatted) return;
    navigator.clipboard.writeText(formatted).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    });
  }

  function handleDownload() {
    const params = { chaveacesso, download: "1" };
    if (embarcadorId) params.embarcadorId = embarcadorId;
    api.get("/autopilot/insights/nf/payload", { params, responseType: "blob" })
      .then(function(res) {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "nf_" + chaveacesso + ".json";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(function(err) { console.error("[Autopilot] Erro ao baixar payload:", err); });
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      fade={false}
      style={{ marginTop: "8vh", marginBottom: "8vh" }}
      contentClassName="cte-details-modal"
      backdropClassName="cte-modal-backdrop"
      wrapClassName="cte-modal-wrap"
    >
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "var(--everlog-card-hero-bg)",
        borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
        padding: "14px 20px",
      }}>
        <span style={{ color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`, fontWeight: 600, fontSize: 14, fontFamily: "monospace" }}>
          {numero ? "NF " + numero + " · " : ""}{chaveacesso || "Payload"}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="text"
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Pesquisar no arquivo"
            disabled={!payload}
            style={{
              background: "var(--everlog-card-surface-field, var(--everlog-card-surface))",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "4px",
              color: AUTOPILOT_COLORS.title,
              fontSize: "0.75rem",
              padding: "3px 8px",
              width: 170,
            }}
          />
          {search ? (
            <span style={{ fontSize: "0.7rem", color: AUTOPILOT_COLORS.textSoft, whiteSpace: "nowrap" }}>
              {matchCount} {matchCount === 1 ? "ocorrência" : "ocorrências"}
            </span>
          ) : null}
          <button
            onClick={handleDownload}
            disabled={!payload}
            style={{
              background: "none",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "4px",
              color: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusWarning} 80%, transparent)`,
              fontSize: "0.75rem",
              padding: "3px 10px",
              cursor: "pointer",
            }}
          >Baixar</button>
          <button
            onClick={handleCopy}
            disabled={!payload}
            style={{
              background: "none",
              border: `1px solid ${AUTOPILOT_COLORS.border}`,
              borderRadius: "4px",
              color: copied ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.textSoft,
              fontSize: "0.75rem",
              padding: "3px 10px",
              cursor: "pointer",
            }}
          >{copied ? "Copiado ✓" : "Copiar"}</button>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.title, fontSize: 24, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
          >×</button>
        </div>
      </div>
      <ModalBody className="everlog-code-viewer" style={{ maxHeight: "65vh", overflow: "auto", padding: "16px 20px" }}>
        {loading && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <ClipLoader size={28} color="var(--everlog-code-tag)" />
          </div>
        )}
        {error && (
          <p style={{ color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.85rem" }}>{error}</p>
        )}
        {payload && !loading && (
          <pre style={{ margin: 0, fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: "0.78rem", lineHeight: "1.6", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {highlightJson(formatted, search)}
          </pre>
        )}
      </ModalBody>
    </Modal>
  );
}

export default PayloadViewerModal;
